import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import {
  PLATFORM_CONTRAT_STATUT_OPTIONS,
  PLATFORM_SCOPE_LABELS,
  platformLocataireNomComplet,
  type PlatformContrat,
  type PlatformContratInput,
  type PlatformContratStatut,
} from "@/store/platform/types";
import { BIEN_TYPE_LABELS, type BienType } from "@/pages/proprietaire/biens/types";

interface PlatformContratFormProps {
  open: boolean;
  onClose: () => void;
  contrat: PlatformContrat | null;
}

interface FormState {
  immeubleId: string;
  bienId: string;
  locataireId: string;
  dateDebut: string;
  dateFin: string;
  loyerMensuel: string;
  caution: string;
  avance: string;
  jourEcheance: string;
  conditionsParticulieres: string;
  document: string;
  statut: PlatformContratStatut;
}

const EMPTY_STATE: FormState = {
  immeubleId: "",
  bienId: "",
  locataireId: "",
  dateDebut: "",
  dateFin: "",
  loyerMensuel: "",
  caution: "",
  avance: "",
  jourEcheance: "",
  conditionsParticulieres: "",
  document: "",
  statut: "brouillon",
};

/**
 * Édition d'un contrat depuis le Super Admin, sur le store unifié.
 * La relation Propriétaire → Immeuble → Bien → Locataire est reconstruite à
 * l'enregistrement à partir de l'immeuble choisi : aucune combinaison
 * incohérente n'est possible. La création reste dans les espaces concernés.
 */
export default function PlatformContratForm({ open, onClose, contrat }: PlatformContratFormProps) {
  const { immeubles, biens, locataires, getImmeuble, getBien, updateContrat } = usePlatformData();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !contrat) return;
    setForm({
      immeubleId: contrat.immeubleId,
      bienId: contrat.bienId,
      locataireId: contrat.locataireId,
      dateDebut: contrat.dateDebut,
      dateFin: contrat.dateFin,
      loyerMensuel: String(contrat.loyerMensuel),
      caution: contrat.caution ? String(contrat.caution) : "",
      avance: contrat.avance ? String(contrat.avance) : "",
      jourEcheance: contrat.jourEcheance ? String(contrat.jourEcheance) : "",
      conditionsParticulieres: contrat.conditionsParticulieres,
      document: contrat.document,
      statut: contrat.statut,
    });
    setErrors({});
  }, [open, contrat]);

  const immeubleOptions = useMemo(
    () =>
      immeubles.map((immeuble) => ({
        value: immeuble.id,
        label: `${immeuble.nom} — ${PLATFORM_SCOPE_LABELS[immeuble.scope]}`,
      })),
    [immeubles]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${BIEN_TYPE_LABELS[bien.type as BienType]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  const locataireOptions = useMemo(
    () => locataires.map((locataire) => ({ value: locataire.id, label: platformLocataireNomComplet(locataire) })),
    [locataires]
  );

  if (!contrat) return null;

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImmeuble = (value: string) => setForm((prev) => ({ ...prev, immeubleId: value, bienId: "" }));

  const handleBien = (value: string) => {
    const bien = getBien(value);
    setForm((prev) => ({
      ...prev,
      bienId: value,
      immeubleId: bien ? bien.immeubleId : prev.immeubleId,
      loyerMensuel: bien && !prev.loyerMensuel ? String(bien.loyerMensuel) : prev.loyerMensuel,
    }));
  };

  const handleDocument = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    update("document", file ? file.name : "");
  };

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.immeubleId) nextErrors.immeubleId = "Sélectionnez l'immeuble.";
    if (!form.bienId) nextErrors.bienId = "Sélectionnez le bien.";
    if (!form.locataireId) nextErrors.locataireId = "Sélectionnez le locataire.";
    if (!form.dateDebut) nextErrors.dateDebut = "Indiquez la date de début.";
    if (!form.loyerMensuel.trim() || Number(form.loyerMensuel) <= 0) {
      nextErrors.loyerMensuel = "Indiquez un loyer mensuel valide.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const immeuble = getImmeuble(form.immeubleId);
    if (!immeuble) {
      setErrors({ immeubleId: "Immeuble introuvable." });
      return;
    }

    const input: PlatformContratInput = {
      scope: immeuble.scope,
      proprietaireId: immeuble.proprietaireId,
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      locataireId: form.locataireId,
      dateDebut: form.dateDebut,
      dateFin: form.dateFin,
      loyerMensuel: Math.max(0, Number(form.loyerMensuel) || 0),
      caution: Math.max(0, Number(form.caution) || 0),
      avance: Math.max(0, Number(form.avance) || 0),
      jourEcheance: Math.min(31, Math.max(1, Math.round(Number(form.jourEcheance) || 1))),
      conditionsParticulieres: form.conditionsParticulieres.trim(),
      document: form.document,
      statut: form.statut,
    };

    updateContrat(contrat.id, input);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Modifier le contrat"
      description="Corrigez les informations du contrat. Le rattachement (espace, propriétaire) est recalculé depuis l'immeuble choisi."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        <Field label="Immeuble" htmlFor="sa-ctr-immeuble" required error={errors.immeubleId}>
          <Select
            id="sa-ctr-immeuble"
            value={form.immeubleId}
            onChange={(event) => handleImmeuble(event.target.value)}
            options={immeubleOptions}
            placeholder="Sélectionnez un immeuble"
            invalid={Boolean(errors.immeubleId)}
          />
        </Field>

        <Field label="Bien" htmlFor="sa-ctr-bien" required error={errors.bienId}>
          <Select
            id="sa-ctr-bien"
            value={form.bienId}
            onChange={(event) => handleBien(event.target.value)}
            options={bienOptions}
            placeholder={form.immeubleId ? "Sélectionnez un bien" : "Choisissez d'abord un immeuble"}
            disabled={!form.immeubleId}
            invalid={Boolean(errors.bienId)}
          />
        </Field>

        <Field label="Locataire" htmlFor="sa-ctr-locataire" required error={errors.locataireId}>
          <Select
            id="sa-ctr-locataire"
            value={form.locataireId}
            onChange={(event) => update("locataireId", event.target.value)}
            options={locataireOptions}
            placeholder="Sélectionnez un locataire"
            invalid={Boolean(errors.locataireId)}
          />
        </Field>

        <Field label="Statut" htmlFor="sa-ctr-statut">
          <Select
            id="sa-ctr-statut"
            value={form.statut}
            onChange={(event) => update("statut", event.target.value)}
            options={PLATFORM_CONTRAT_STATUT_OPTIONS}
          />
        </Field>

        <Field label="Date de début" htmlFor="sa-ctr-debut" required error={errors.dateDebut}>
          <Input
            id="sa-ctr-debut"
            type="date"
            value={form.dateDebut}
            onChange={(event) => update("dateDebut", event.target.value)}
            invalid={Boolean(errors.dateDebut)}
          />
        </Field>

        <Field label="Date de fin" htmlFor="sa-ctr-fin">
          <Input
            id="sa-ctr-fin"
            type="date"
            value={form.dateFin}
            onChange={(event) => update("dateFin", event.target.value)}
          />
        </Field>

        <Field label="Loyer mensuel (FCFA)" htmlFor="sa-ctr-loyer" required error={errors.loyerMensuel}>
          <Input
            id="sa-ctr-loyer"
            type="number"
            min={0}
            max={100000000}
            value={form.loyerMensuel}
            onChange={(event) => update("loyerMensuel", event.target.value)}
            invalid={Boolean(errors.loyerMensuel)}
          />
        </Field>

        <Field label="Jour d'échéance" htmlFor="sa-ctr-echeance" hint="Jour du mois (1 à 31).">
          <Input
            id="sa-ctr-echeance"
            type="number"
            min={1}
            max={31}
            step={1}
            value={form.jourEcheance}
            onChange={(event) => update("jourEcheance", event.target.value)}
          />
        </Field>

        <Field label="Caution (FCFA)" htmlFor="sa-ctr-caution">
          <Input
            id="sa-ctr-caution"
            type="number"
            min={0}
            max={100000000}
            value={form.caution}
            onChange={(event) => update("caution", event.target.value)}
          />
        </Field>

        <Field label="Avance (FCFA)" htmlFor="sa-ctr-avance">
          <Input
            id="sa-ctr-avance"
            type="number"
            min={0}
            max={100000000}
            value={form.avance}
            onChange={(event) => update("avance", event.target.value)}
          />
        </Field>

        <Field label="Document du contrat" htmlFor="sa-ctr-document" hint="PDF ou image (le fichier reste local).">
          <Input
            id="sa-ctr-document"
            type="file"
            accept="application/pdf,image/*"
            onChange={handleDocument}
            className="file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-background-100 file:px-3 file:py-1.5 file:text-sm file:font-label file:text-foreground-800"
          />
        </Field>

        <Field label="Conditions particulières" htmlFor="sa-ctr-conditions" className="sm:col-span-2">
          <Textarea
            id="sa-ctr-conditions"
            value={form.conditionsParticulieres}
            onChange={(event) => update("conditionsParticulieres", event.target.value)}
            className="min-h-[88px]"
            maxLength={600}
          />
        </Field>
      </div>
    </Modal>
  );
}